import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { FiEdit2, FiCheck, FiX, FiTrash2, FiImage } from 'react-icons/fi';
import DevFixtures from './DevFixtures';

type MedicineData = {
  logs: string[];
  notes: string;
  images: string[];
};
type MedicineStore = Record<string, MedicineData>;

const STORAGE_KEY = 'medicine-tracker';

const COLORS: [string, string][] = [
  ['bg-blue-400',    'text-blue-400'],
  ['bg-emerald-400', 'text-emerald-400'],
  ['bg-violet-400',  'text-violet-400'],
  ['bg-amber-400',   'text-amber-400'],
  ['bg-rose-400',    'text-rose-400'],
  ['bg-cyan-400',    'text-cyan-400'],
];

function load(): MedicineStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    const out: MedicineStore = {};
    if (Array.isArray(parsed?.medicines)) {
      for (const e of parsed.medicines) {
        if (e.name && e.lastLogged) {
          out[e.name] = { logs: [e.lastLogged], notes: '', images: [] };
        }
      }
    } else {
      for (const [key, val] of Object.entries(parsed)) {
        if (Array.isArray(val)) {
          out[key] = { logs: val as string[], notes: '', images: [] };
        } else if (val && typeof val === 'object' && 'logs' in val) {
          const d = val as Partial<MedicineData>;
          out[key] = { logs: d.logs ?? [], notes: d.notes ?? '', images: d.images ?? [] };
        }
      }
    }
    return out;
  } catch {
    return {};
  }
}

function persist(data: MedicineStore) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function formatElapsed(iso: string): string {
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const s = Math.floor(diff / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h >= 24) {
    const days = Math.floor(h / 24);
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }
  if (h > 0) return `${h}h ${m}m ago`;
  if (m > 0) return `${m}m ${sec}s ago`;
  return `${sec}s ago`;
}

function toLocalDT(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const iconBtn = 'text-zinc-700 hover:text-zinc-400 transition-colors duration-150';

type ModalState = { med: string; nameValue: string };

export default function MedicineTracker() {
  const [store, setStore] = useState<MedicineStore>(load);
  const [name, setName] = useState('');
  const [time, setTime] = useState(() => toLocalDT(new Date()));
  const [, tick] = useState(0);
  const timeManuallyEdited = useRef(false);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modal, setModal] = useState<ModalState | null>(null);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [editingEntry, setEditingEntry] = useState<{ med: string; iso: string; value: string } | null>(null);

  const entryInputRef = useRef<HTMLInputElement>(null);
  const modalNameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const id = setInterval(() => {
      tick(n => n + 1);
      if (!timeManuallyEdited.current) setTime(toLocalDT(new Date()));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => { entryInputRef.current?.focus(); }, [editingEntry?.iso]);

  const medicines = Object.keys(store);
  const colorIndex = Object.fromEntries(medicines.map((m, i) => [m, i % COLORS.length]));

  function update(next: MedicineStore) {
    persist(next);
    setStore(next);
  }

  // ── modal open/close ──────────────────────────────────────────────────────
  const openModal = (med: string) => setModal({ med, nameValue: med });

  const closeModal = () => {
    if (!modal) return;
    const { med, nameValue } = modal;
    setModal(null);
    const trimmed = nameValue.trim();
    if (trimmed && trimmed !== med && !store[trimmed]) {
      const { [med]: data, ...rest } = store;
      update({ ...rest, [trimmed]: data });
    }
  };

  // ── add new medicine ──────────────────────────────────────────────────────
  const handleAdd = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    const iso = new Date(time).toISOString();
    const existing = store[trimmed] ?? { logs: [], notes: '', images: [] };
    update({ ...store, [trimmed]: { ...existing, logs: [...existing.logs, iso] } });
    setName('');
    setTime(toLocalDT(new Date()));
    timeManuallyEdited.current = false;
  };

  // ── quick-log existing medicine ───────────────────────────────────────────
  const handleLog = (med: string) => {
    const data = store[med];
    update({ ...store, [med]: { ...data, logs: [...data.logs, new Date().toISOString()] } });
  };

  // ── delete whole medicine ─────────────────────────────────────────────────
  const deleteMedicine = (med: string) => {
    setModal(null);
    const { [med]: _, ...rest } = store;
    update(rest);
    if (editingEntry?.med === med) setEditingEntry(null);
  };

  // ── delete single entry ───────────────────────────────────────────────────
  const deleteEntry = (med: string, iso: string) => {
    const data = store[med];
    const logs = data.logs.filter(t => t !== iso);
    if (logs.length === 0 && !data.notes && data.images.length === 0) {
      const { [med]: _, ...rest } = store;
      update(rest);
    } else {
      update({ ...store, [med]: { ...data, logs } });
    }
    if (editingEntry?.med === med && editingEntry?.iso === iso) setEditingEntry(null);
  };

  // ── edit single entry time ────────────────────────────────────────────────
  const saveEntry = () => {
    if (!editingEntry) return;
    const { med, iso, value } = editingEntry;
    setEditingEntry(null);
    const newIso = new Date(value).toISOString();
    const data = store[med];
    update({ ...store, [med]: { ...data, logs: data.logs.map(t => t === iso ? newIso : t) } });
  };

  // ── notes & images ────────────────────────────────────────────────────────
  const updateNotes = (med: string, notes: string) => {
    update({ ...store, [med]: { ...store[med], notes } });
  };

  const addImage = (med: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result as string;
      try {
        const data = store[med];
        update({ ...store, [med]: { ...data, images: [...data.images, dataUrl] } });
      } catch {
        alert('Not enough storage space for this image. Try a smaller file.');
      }
    };
    reader.readAsDataURL(file);
  };

  const removeImage = (med: string, idx: number) => {
    const data = store[med];
    update({ ...store, [med]: { ...data, images: data.images.filter((_, i) => i !== idx) } });
  };

  const isEmpty = medicines.length === 0;

  // current data for the open modal (keyed by original med name)
  const modalData = modal ? store[modal.med] : null;
  const modalColor = modal ? COLORS[colorIndex[modal.med] ?? 0][1] : '';

  return (
    <div className="fixed inset-0 bg-[#09090b] overflow-y-auto">
      <Helmet>
        <title>Medicine Tracker</title>
        <meta name="description" content="Log when you last took your medicine" />
      </Helmet>

      <div className="min-h-full flex flex-col items-center justify-center p-8 gap-10">
        <Link to="/" className="absolute top-8 left-8 text-zinc-600 hover:text-zinc-400 text-sm transition-colors duration-150">
          ← back
        </Link>

        <p className="text-zinc-400 text-xs tracking-widest uppercase self-center">
          medicine tracker
        </p>

        {/* Per-medicine trees */}
        {!isEmpty && (
          <div className="flex flex-wrap gap-10 justify-center">
            {medicines.map(med => {
              const [dot, text] = COLORS[colorIndex[med]];
              const data = store[med];
              const sorted = [...data.logs].sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

              return (
                <div key={med} className="flex flex-col items-start gap-3">

                  {/* Tappable medicine name */}
                  <button
                    onClick={() => openModal(med)}
                    className={`text-xs font-medium tracking-wide ${text} hover:opacity-70 transition-opacity duration-150`}
                  >
                    {med}
                  </button>

                  {/* Log button */}
                  <button
                    onClick={() => handleLog(med)}
                    className={`text-xs px-3 py-1 border border-zinc-800 rounded-full transition-colors duration-150 hover:border-zinc-600 ${text}`}
                  >
                    log
                  </button>

                  {/* Tree nodes */}
                  {(() => {
                    const COLLAPSE_AT = 3;
                    const isExpanded = expanded.has(med);
                    const visible = (!isExpanded && sorted.length >= COLLAPSE_AT)
                      ? sorted.slice(0, 2)
                      : sorted;
                    const hasMore = !isExpanded && sorted.length >= COLLAPSE_AT;
                    return (
                      <div className="relative">
                        {visible.length > 1 && (
                          <div className="absolute left-[9px] top-2 bottom-2 w-px bg-zinc-800" />
                        )}
                        {visible.map(iso => {
                          const d = new Date(iso);
                          const timeLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          const isEditingThis = editingEntry?.med === med && editingEntry?.iso === iso;

                          return (
                            <div key={iso} className="flex items-start gap-2 pb-4 last:pb-0">
                              <div className="w-[18px] shrink-0 flex justify-center pt-[3px] z-10">
                                <div className={`w-2.5 h-2.5 rounded-full ring-2 ring-[#09090b] ${dot}`} />
                              </div>

                              {isEditingThis ? (
                                <div className="flex items-center gap-2 pt-[1px]">
                                  <input
                                    ref={entryInputRef}
                                    type="datetime-local"
                                    value={editingEntry.value}
                                    onChange={e => setEditingEntry({ med, iso, value: e.target.value })}
                                    onKeyDown={e => { if (e.key === 'Enter') saveEntry(); if (e.key === 'Escape') setEditingEntry(null); }}
                                    className="bg-transparent border-b border-zinc-600 text-zinc-400 text-xs focus:outline-none w-40"
                                  />
                                  <button onClick={saveEntry} className={iconBtn}><FiCheck size={11} /></button>
                                  <button onClick={() => setEditingEntry(null)} className={iconBtn}><FiX size={11} /></button>
                                </div>
                              ) : (
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-2">
                                    <span className="text-zinc-500 text-xs whitespace-nowrap">{formatElapsed(iso)}</span>
                                    <button
                                      onClick={() => setEditingEntry({ med, iso, value: toLocalDT(new Date(iso)) })}
                                      className={iconBtn} title="Edit time"
                                    >
                                      <FiEdit2 size={10} />
                                    </button>
                                    <button onClick={() => deleteEntry(med, iso)} className={iconBtn} title="Delete entry">
                                      <FiX size={10} />
                                    </button>
                                  </div>
                                  <span className="text-zinc-700 text-xs">{timeLabel}</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {hasMore && (
                          <div className="relative mt-1">
                            <div className="absolute -top-8 left-0 right-0 h-8 bg-gradient-to-t from-[#09090b] to-transparent pointer-events-none" />
                            <button
                              onClick={() => setExpanded(prev => new Set([...prev, med]))}
                              className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors duration-150"
                            >
                              see {sorted.length - 2} more
                            </button>
                          </div>
                        )}
                        {isExpanded && sorted.length >= COLLAPSE_AT && (
                          <button
                            onClick={() => setExpanded(prev => { const s = new Set(prev); s.delete(med); return s; })}
                            className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors duration-150 mt-1"
                          >
                            see less
                          </button>
                        )}
                      </div>
                    );
                  })()}

                </div>
              );
            })}
          </div>
        )}

        {/* Add input */}
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <input
            type="text"
            placeholder={isEmpty ? 'medicine name' : 'add another medicine'}
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            className="bg-transparent border border-zinc-700 text-zinc-200 placeholder-zinc-600 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-zinc-500 transition-colors duration-150"
          />
          <div className="flex gap-2">
            <input
              type="datetime-local"
              value={time}
              onChange={e => { timeManuallyEdited.current = true; setTime(e.target.value); }}
              className="flex-1 min-w-0 bg-transparent border border-zinc-700 text-zinc-400 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-zinc-500 transition-colors duration-150"
            />
            <button
              onClick={handleAdd}
              disabled={!name.trim()}
              className="px-4 py-2 border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm rounded-lg transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              add
            </button>
          </div>
          {isEmpty && (
            <p className="text-zinc-700 text-xs text-center mt-1">no medicines logged yet</p>
          )}
        </div>
      </div>

      {/* Medicine modal */}
      {modal && modalData && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
          onClick={closeModal}
        >
          <div
            className="bg-[#111113] border border-zinc-800 rounded-2xl w-full max-w-sm flex flex-col gap-5 p-5"
            onClick={e => e.stopPropagation()}
          >
            {/* Name + close */}
            <div className="flex items-center gap-3">
              <input
                ref={modalNameRef}
                value={modal.nameValue}
                onChange={e => setModal({ ...modal, nameValue: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') closeModal(); if (e.key === 'Escape') closeModal(); }}
                className={`flex-1 bg-transparent text-base font-semibold focus:outline-none border-b border-transparent focus:border-zinc-600 transition-colors duration-150 pb-0.5 ${modalColor}`}
              />
              <button onClick={closeModal} className={iconBtn}>
                <FiX size={16} />
              </button>
            </div>

            {/* Notes */}
            <div className="flex flex-col gap-2">
              <p className="text-zinc-600 text-xs uppercase tracking-widest">notes</p>
              <textarea
                value={modalData.notes}
                onChange={e => updateNotes(modal.med, e.target.value)}
                placeholder="dosage, instructions, notes..."
                rows={4}
                className="w-full bg-zinc-900/60 border border-zinc-800 text-zinc-300 placeholder-zinc-700 text-sm rounded-xl px-3 py-2.5 focus:outline-none focus:border-zinc-600 transition-colors duration-150 resize-none"
              />
            </div>

            {/* Images */}
            <div className="flex flex-col gap-2">
              <p className="text-zinc-600 text-xs uppercase tracking-widest">images</p>
              {modalData.images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {modalData.images.map((src, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={src}
                        alt=""
                        onClick={() => setLightbox(src)}
                        className="w-20 h-20 object-cover rounded-xl border border-zinc-800 cursor-zoom-in"
                      />
                      <button
                        onClick={() => removeImage(modal.med, idx)}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-zinc-900 border border-zinc-700 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-300 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      >
                        <FiX size={9} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer text-zinc-600 hover:text-zinc-400 transition-colors duration-150 text-xs">
                <FiImage size={13} />
                <span>add image</span>
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) addImage(modal.med, file);
                    e.target.value = '';
                  }}
                />
              </label>
            </div>

            {/* Delete */}
            <div className="border-t border-zinc-800 pt-4">
              <button
                onClick={() => deleteMedicine(modal.med)}
                className="flex items-center gap-2 text-xs text-zinc-600 hover:text-rose-400 transition-colors duration-150"
              >
                <FiTrash2 size={13} />
                delete {modal.med}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox}
            alt=""
            className="max-w-full max-h-full rounded-xl object-contain"
          />
        </div>
      )}

      {process.env.NODE_ENV === 'development' && <DevFixtures />}
    </div>
  );
}
